import { Loader2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { GstVerificationScreen } from '@/features/workflows/components/simple/preview-screens';
import { toast } from '@/lib/notify';

import { useVerifyStep } from '../../api/useVerifyStep';
import type {
  SessionDetailDto,
  SessionStepApiBindingDto,
  SessionStepDto,
} from '../../schemas/sessionSchemas';
import { extractReason } from '../pan/pan.utils';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';
import {
  CHECK_FAILED_TOAST,
  RETRY_EXHAUSTED_NOTE,
  tryAnotherMethodNote,
} from '../stepFallbackNotes';
import { GstDevSimulator } from './GstDevSimulator';
import { isValidGstin } from './gst.utils';

// Fallback binding for legacy sessions whose snapshot carries no bindings —
// submits without a catalogueEntryId, letting the backend run its own chain.
const DEFAULT_BINDING: SessionStepApiBindingDto = {
  catalogueEntryId: '',
  path: '/api/v1/GST/verify',
  operationId: 'Post_Gst_Verify',
  summary: 'GST Verification',
  priority: 1,
};

/**
 * Runs one GST Verification step in a live session. The applicant enters their
 * GSTIN; the backend verifier injects the consent trio and calls the Whitebooks
 * basic GSTIN lookup (the only method Studio offers). On a technical failure the
 * runner shows an error — same fallback-loop shape as the PAN / Aadhaar / Bank
 * runners, though GST has only one method to fall through to.
 */
export function GstStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const { mutate, isPending } = useVerifyStep(session.id);

  const bindings = useMemo(() => {
    const b = step.apiBindings.slice().sort((x, y) => x.priority - y.priority);
    return b.length > 0 ? b : [DEFAULT_BINDING];
  }, [step.apiBindings]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [gstNumber, setGstNumber] = useState('');
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const active = bindings[activeIdx] ?? bindings[0] ?? DEFAULT_BINDING;

  const gstValid = isValidGstin(gstNumber);
  const ready = gstValid;

  if (expired || session.isExpired) {
    return <SessionExpiredScreen />;
  }

  // Business reject (step Failed) → terminal rejection screen (cancelled GSTIN or
  // a Reject risk-rule / condition). A reject ends the session — no retry.
  if (step.status === 'Failed') {
    return <FailureScreen reason={extractReason(step.resultJson)} />;
  }

  function buildInput(): Record<string, unknown> {
    // Applicant data only — the backend verifier's NormalizeInput injects the
    // consent trio and the name-match reference comes from an earlier PAN step
    // (engine-injected). Field name matches the gateway request (gst_no).
    return { gst_no: gstNumber.trim().toUpperCase() };
  }

  function handleSubmit() {
    if (!ready) return;
    submitInput(buildInput());
  }

  // DEV: one-click scenario — submits a `test_case` (backend GstMockResponses
  // returns the canned result) through the SAME flow so rules/fallback run.
  function runTestCase(testCase: string) {
    submitInput({ gst_no: '', test_case: testCase });
  }

  function submitInput(input: Record<string, unknown>) {
    mutate(
      {
        stepId: step.id,
        input,
        catalogueEntryId: active.catalogueEntryId || undefined,
      },
      {
        onSuccess: (data) => {
          const updated = data.steps.find((s) => s.id === step.id);
          if (!updated) return;
          const status = updated?.status;
          if (status === 'Passed' || status === 'Review' || status === 'Failed') {
            // Final verdict — the parent advances / shows the failure screen.
            return;
          }
          // status Pending → technical provider failure → visible fallback.
          const nextBinding = bindings[activeIdx + 1];
          if (nextBinding) {
            // Applicant-facing: never the provider's internal name (`summary`).
            // Same wording as the other runners.
            setFallbackNote(tryAnotherMethodNote());
            setActiveIdx(activeIdx + 1);
          } else {
            toast.error(CHECK_FAILED_TOAST);
            setFallbackNote(RETRY_EXHAUSTED_NOTE);
            setActiveIdx(0);
          }
        },
        onError: (error) => {
          if (isSessionExpiredError(error)) {
            setExpired(true);
            return;
          }
          toast.error('Could not reach the server. Please try again.');
        },
      },
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      {/* DEV-ONLY: one-click GST scenarios (backend GstMockResponses) to exercise
          the step's Risk / Advanced Rules. Stripped from prod. */}
      {import.meta.env.DEV && <GstDevSimulator onRun={runTestCase} isPending={isPending} />}

      {fallbackNote && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
          {fallbackNote}
        </div>
      )}

      <GstVerificationScreen
        method={active.summary}
        values={{ gstNumber }}
        onChange={(patch) => {
          if (patch.gstNumber !== undefined) setGstNumber(patch.gstNumber);
        }}
        gstError={
          gstNumber.length > 0 && !gstValid
            ? 'Enter a valid 15-character GSTIN (e.g. 27AAAPL0000A1Z5).'
            : undefined
        }
      />

      <button
        type="button"
        onClick={handleSubmit}
        disabled={isPending || !ready}
        style={{
          backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
          color: 'var(--brand-btn-text, #ffffff)',
          borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
          borderRadius: 'var(--brand-btn-radius, 0.75rem)',
        }}
        className="mt-auto flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
      >
        {isPending && <Loader2 size={15} className="animate-spin" />}
        Continue
      </button>
    </div>
  );
}

// Shown when a GST step is rejected (business Failed) — e.g. a cancelled GSTIN
// or a Reject risk-rule fired. Terminal.
function FailureScreen({ reason }: { reason?: string | null }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
      <div className="mb-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#DC2626]">
        <X size={60} strokeWidth={3} color="white" />
      </div>
      <h2 className="mb-2 text-[length:var(--text-20)] font-semibold leading-tight text-[#DC2626]">
        GST verification failed
      </h2>
      <p className="text-[length:var(--text-14)] text-[#666666]">
        {reason ?? 'Your GSTIN could not be verified. This session has been rejected.'}
      </p>
    </div>
  );
}
